import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { BeneficiaryAllocation } from "./constructionPoDocument";

export type PoAnnexurePdfInput = {
  allocations: BeneficiaryAllocation[];
  accentColorHex: string;
  footerNote?: string;
};

type JsPdfWithAutoTable = jsPDF & { lastAutoTable?: { finalY: number } };

function fmtInr(n: number): string {
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function hexToRgb(hex: string): [number, number, number] {
  const t = hex.trim().replace(/^#/, "");
  if (t.length !== 6) return [22, 54, 95];
  return [parseInt(t.slice(0, 2), 16), parseInt(t.slice(2, 4), 16), parseInt(t.slice(4, 6), 16)];
}

/** Landscape annexure pages (vector) — avoids a second slow html2canvas pass. */
export function appendPoAnnexureVector(
  pdf: jsPDF,
  input: PoAnnexurePdfInput,
  opts?: { addLandscapePage?: boolean },
): void {
  if (!input.allocations.length) return;

  const accent = hexToRgb(input.accentColorHex);
  const doc = pdf as JsPdfWithAutoTable;

  if (opts?.addLandscapePage !== false) {
    pdf.addPage("a4", "l");
  }
  const landW = pdf.internal.pageSize.getWidth();
  const marginL = 12;
  const marginR = 12;
  let y = 14;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(accent[0], accent[1], accent[2]);
  pdf.text("ANNEXURE: BENEFICIARY ALLOCATION", marginL, y);
  y += 8;

  const usableW = landW - marginL - marginR;
  /** One S.No per beneficiary (rowspan); application / geo columns rowspan; material rows only in last two columns. */
  type BodyCell = string | { content: string; rowSpan?: number; styles?: Record<string, unknown> };
  const body: BodyCell[][] = [];
  let groupNo = 1;
  for (const alloc of input.allocations) {
    const n = alloc.items.length;
    if (n === 0) continue;
    for (let i = 0; i < n; i++) {
      const item = alloc.items[i];
      if (i === 0) {
        body.push([
          {
            content: String(groupNo),
            rowSpan: n,
            styles: { valign: "middle", halign: "center", fontStyle: "bold", fontSize: 8 },
          },
          {
            content: alloc.beneficiaryName,
            rowSpan: n,
            styles: { valign: "middle", fontStyle: "bold", fontSize: 7.5 },
          },
          {
            content: alloc.applicationId,
            rowSpan: n,
            styles: { valign: "middle", fontStyle: "normal", fontSize: 7 },
          },
          { content: alloc.district, rowSpan: n, styles: { valign: "middle", fontSize: 7 } },
          { content: alloc.mandal, rowSpan: n, styles: { valign: "middle", fontSize: 7 } },
          { content: alloc.village, rowSpan: n, styles: { valign: "middle", fontSize: 7 } },
          item.label,
          `${fmtInr(item.qty)} ${item.unit}`,
        ]);
      } else {
        body.push([item.label, `${fmtInr(item.qty)} ${item.unit}`]);
      }
    }
    groupNo += 1;
  }

  const colSno = 9;
  const colBen = 34;
  const colApp = 30;
  const colDist = 22;
  const colMandal = 22;
  const colVillage = 22;
  const colQty = 24;
  const colMat = Math.max(
    36,
    usableW - colSno - colBen - colApp - colDist - colMandal - colVillage - colQty,
  );

  autoTable(pdf, {
    startY: y,
    margin: { left: marginL, right: marginR },
    head: [
      [
        "S.NO",
        "BENEFICIARY NAME",
        "APPLICATION ID",
        "DISTRICT",
        "MANDAL",
        "VILLAGE",
        "MATERIAL",
        "ALLOCATED QTY",
      ],
    ],
    body,
    styles: {
      fontSize: 7.5,
      cellPadding: 2,
      lineColor: [226, 232, 240],
      lineWidth: 0.15,
      valign: "top",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: accent,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 6.8,
      cellPadding: 2.5,
      valign: "middle",
    },
    columnStyles: {
      0: { cellWidth: colSno, halign: "center" },
      1: { cellWidth: colBen },
      2: { cellWidth: colApp, fontStyle: "normal" },
      3: { cellWidth: colDist },
      4: { cellWidth: colMandal },
      5: { cellWidth: colVillage },
      6: { cellWidth: colMat },
      7: { cellWidth: colQty, halign: "right" },
    },
    theme: "grid",
    tableWidth: usableW,
  });

  y = doc.lastAutoTable?.finalY ?? y + 20;
  y += 4;
  const foot = input.footerNote?.trim() || "Generated from the HO console.";
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(7);
  pdf.setTextColor(100, 116, 139);
  pdf.text(foot.length > 220 ? `${foot.slice(0, 217)}…` : foot, marginL, y);
}

/** Standalone landscape PDF (annexure only) — for merging after server-rendered main PO. */
export function buildPoAnnexurePdfBlob(input: PoAnnexurePdfInput): Blob {
  if (!input.allocations.length) throw new Error("No annexure rows to export.");
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
  appendPoAnnexureVector(pdf, input, { addLandscapePage: false });
  const out = pdf.output("blob");
  if (!(out instanceof Blob)) throw new Error("Annexure PDF export failed.");
  return out;
}
